import * as Headless from '@headlessui/react'
import type React from 'react'
import { forwardRef } from 'react'
import { cn } from '../../shared/cn'
import { TouchTarget } from './Button'
import { Link } from './Link'

type AvatarProps = {
  src?: string | null
  square?: boolean
  initials?: string
  alt?: string
  className?: string
  imageResolution?: number
  priority?: boolean
}

export function Avatar({
  src = null,
  imageResolution = 64,
  square = false,
  initials,
  alt = '',
  className = 'size-8 bg-algonavy-500 text-white',
  priority = false,
  ...props
}: AvatarProps & React.ComponentPropsWithoutRef<'span'>) {
  return (
    <span
      data-slot='avatar'
      {...props}
      className={cn(
        className,
        // Basic layout
        'inline-grid shrink-0 align-middle [--avatar-radius:20%] *:col-start-1 *:row-start-1',
        'outline outline-black/10 -outline-offset-1 dark:outline-white/10',
        // Border radius
        square
          ? 'rounded-(--avatar-radius) *:rounded-(--avatar-radius)'
          : 'rounded-full *:rounded-full'
      )}
    >
      {initials && (
        <svg
          className='select-none fill-current p-[5%] font-medium text-[48px] uppercase'
          viewBox='0 0 100 100'
          aria-hidden='true'
        >
          <text
            x='50%'
            y='50%'
            alignmentBaseline='middle'
            dominantBaseline='middle'
            textAnchor='middle'
            dy='.125em'
          >
            {initials}
          </text>
        </svg>
      )}
      {src && (
        <img
          className='size-full'
          src={src}
          alt={alt}
          height={imageResolution}
          width={imageResolution}
          loading={priority ? 'eager' : 'lazy'}
        />
      )}
    </span>
  )
}

type AvatarButtonLinkProps = AvatarProps &
  Omit<React.ComponentPropsWithoutRef<typeof Link>, 'className'> & {
    href: string
  }

type AvatarButtonButtonProps = AvatarProps & Omit<Headless.ButtonProps, 'as' | 'className'>

export const AvatarButton = forwardRef(function AvatarButton(
  {
    src,
    square = false,
    initials,
    alt,
    className,
    ...props
  }: AvatarButtonLinkProps | AvatarButtonButtonProps,
  ref: React.ForwardedRef<HTMLElement>
) {
  const classes = cn(
    'relative inline-grid focus:outline-hidden data-focus:outline-2 data-focus:outline-offset-2 data-focus:outline-blue-500 ',
    square ? 'rounded-[20%]' : 'rounded-full',
    className
  )

  if ('href' in props && typeof props.href === 'string') {
    const linkProps = props as AvatarButtonLinkProps
    return (
      <Link {...linkProps} className={classes} ref={ref as React.ForwardedRef<HTMLAnchorElement>}>
        <TouchTarget>
          <Avatar src={src} square={square} initials={initials} alt={alt} />
        </TouchTarget>
      </Link>
    )
  }

  const buttonProps = props as AvatarButtonButtonProps
  return (
    <Headless.Button {...buttonProps} className={classes} ref={ref}>
      <TouchTarget>
        <Avatar src={src} square={square} initials={initials} alt={alt} />
      </TouchTarget>
    </Headless.Button>
  )
})
